import styled, { keyframes } from "styled-components";

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  text-transform: uppercase;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 30px;
  animation: ${slideIn} 0.6s ease-out;
`;

export const ProfileHeader = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 40px;
  animation: ${slideIn} 0.6s ease-out 0.2s both;

  @media (max-width: 1200px) {
    gap: 20px;
  }
`;

export const ProfileCard = styled.div`
  background: var(--surface);
  border: 2px solid var(--accent-light);
  border-radius: 25px;
  padding: 40px;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 30px;
  position: relative;
  width: 100%;
  box-shadow: var(--shadow-soft);

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 20px;
  }
`;

export const AvatarSection = styled.div`
  position: relative;
  z-index: 2;
`;

export const AvatarContainer = styled.div`
  position: relative;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

export const Avatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: var(--surface);
  font-weight: 700;
  border: 2px solid var(--accent-light);
`;

export const AvatarOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${AvatarContainer}:hover & {
    opacity: 1;
  }
`;

export const CameraIcon = styled.div`
  font-size: 24px;
  color: white;
`;

export const UserInfo = styled.div`
  flex: 1;
  position: relative;
  z-index: 2;
`;

export const UserName = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  @media (max-width: 768px) {
    text-align: center;
  }
`;

export const UserRole = styled.p`
  font-size: 16px;
  opacity: 0.9;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  @media (max-width: 768px) {
    text-align: center;
  }
`;

export const JoinDate = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  opacity: 0.8;
  @media (max-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
    text-align: center;
  }
`;

export const EditButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: var(--surface);
  padding: 12px 20px;
  border-radius: 15px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-2px);
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const StatCard = styled.div`
  background: var(--surface);
  padding: 25px 20px;
  border-radius: 20px;
  border: 2px solid var(--accent-light);
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-soft);
    border-color: var(--accent);
    background: rgba(124, 14, 51, 0.02);
  }
`;

export const StatIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: var(--surface);
  margin: 0 auto 15px;

  ${(props) => {
    switch (props.$type) {
      case "courses":
        return "background: var(--gradient-primary);";
      case "certificates":
        return "background: var(--warning);";
      case "time":
        return "background: var(--success);";
      case "streak":
        return "background: linear-gradient(135deg, var(--accent), var(--accent-dark));";
      default:
        return "background: var(--accent);";
    }
  }}
`;

export const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 8px;
`;

export const StatLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  color: var(--text-secondary);
  font-weight: 600;
`;

export const ProfileContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

export const Section = styled.div`
  background: var(--surface);
  border-radius: 20px;
  padding: 30px;
  border: 2px solid var(--accent-light);
  animation: ${slideIn} 0.6s ease-out;
  transition: all 0.3s ease;
`;

export const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--text-primary);
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--accent-light);
`;

export const SectionSubtitle = styled.h4`
  margin: 0 0 20px 0;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
  text-align: center;
`;

export const InfoGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  gap: 20px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

export const InfoItem = styled.div`
  background: var(--surface);
  padding: 20px;
  border-radius: 15px;
  min-width: 250px;
  border: 1px solid var(--accent-light);
  transition: all 0.3s ease;
  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
  }
`;

export const InfoLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 8px;
`;

export const InfoValue = styled.div`
  font-size: 16px;
  color: var(--text-primary);
  font-weight: 500;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const EditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
export const PasswordGroup = styled.div`
  position: relative;
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 5px;
  transition: color 0.3s ease;

  &:hover {
    color: var(--accent);
  }
`;
export const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--text-secondary);
  letter-spacing: 1px;
`;

export const Input = styled.input`
  padding: 15px 20px;
  border: 2px solid var(--accent-light);
  border-radius: 15px;
  font-family: inherit;
  font-size: 14px;
  background: var(--surface);
  color: var(--text-primary);
  width: 100%;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(124, 14, 51, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const TextArea = styled.textarea`
  padding: 15px 20px;
  border: 2px solid var(--accent-light);
  border-radius: 15px;
  font-family: inherit;
  font-size: 14px;
  background: var(--surface);
  color: var(--text-primary);
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(124, 14, 51, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const FormActions = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-top: 10px;
`;

export const SaveButton = styled.button`
  background: var(--gradient-primary);
  border: none;
  color: var(--surface);
  padding: 12px 25px;
  border-radius: 15px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: var(--accent-dark);
    color: var(--surface);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    filter: grayscale(0.4);
    cursor: not-allowed;
  }
`;

export const CancelButton = styled.button`
  background: transparent;
  border: 2px solid var(--text-muted);
  color: var(--text-muted);
  padding: 12px 25px;
  border-radius: 15px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    transform: translateY(-2px);
  }
`;

export const AchievementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
`;

export const AchievementCard = styled.div`
  background: var(--surface);
  padding: 25px;
  border-radius: 15px;
  border: 2px solid
    ${(props) => (props.$earned ? "var(--accent)" : "var(--accent-light)")};
  text-align: center;
  transition: all 0.3s ease;
  opacity: ${(props) => (props.$earned ? 1 : 0.7)};

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-soft);
    background: ${(props) =>
      props.$earned ? "rgba(124, 14, 51, 0.02)" : "rgba(124, 14, 51, 0.01)"};
    border-color: var(--accent);
  }
`;

export const AchievementIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${(props) =>
    props.$earned ? "var(--gradient-primary)" : "var(--text-muted)"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: var(--surface);
  margin: 0 auto 15px;
`;

export const AchievementTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 8px;
  text-transform: uppercase;
`;

export const AchievementDescription = styled.p`
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.4;
  margin-bottom: 10px;
`;

export const AchievementDate = styled.div`
  font-size: 11px;
  color: var(--text-muted);
  text-transform: uppercase;
  font-weight: 600;
`;

// Date Picker Components
export const DatePickerContainer = styled.div`
  display: flex;
  gap: 10px;
`;

// Email Verification Components
export const EmailStatusContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  font-size: 14px;
  width: 100%;
`;

export const EmailStatusText = styled.span`
  color: ${(props) => (props.$verified ? "#2e7d32" : "#8a6d1a")};
  font-weight: 600;
`;

export const EmailStatusIcon = styled.div`
  color: ${(props) => (props.$verified ? "#2e7d32" : "#8a6d1a")};
  margin-right: 8px;
`;

export const VerifyEmailButton = styled.button`
  background: var(--gradient-primary);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--shadow-medium);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:active {
    transform: translateY(0);
  }
`;

export const DisabledInput = styled(Input)`
  background-color: #f5f5f5;
  cursor: not-allowed;
  color: var(--text-secondary);
`;

export const EmailInfoValue = styled(InfoValue)`
  display: flex;
  align-items: center;
`;

export const EmailInfoIcon = styled.div`
  color: ${(props) => (props.$verified ? "#2e7d32" : "#8a6d1a")};
`;

// Orders & Certificates Sections
export const SectionLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
`;

export const SectionMessage = styled.p`
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
`;

export const RecordsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

export const RecordCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--accent-light);
  border-radius: 15px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--accent);
    box-shadow: var(--shadow-soft);
  }
`;

export const RecordHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

export const RecordTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
`;

export const RecordDate = styled.div`
  font-size: 12px;
  color: var(--text-muted);
`;

export const RecordDetails = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.4;
`;

export const RecordFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

export const RecordAmount = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${(props) => {
    switch (props.$status) {
      case "completed":
      case "approved":
        return "background: rgba(46, 125, 50, 0.1); color: #2e7d32;";
      case "pending":
      case "processing":
        return "background: rgba(138, 109, 26, 0.1); color: #8a6d1a;";
      case "failed":
      case "canceled":
      case "rejected":
        return "background: rgba(198, 40, 40, 0.1); color: #c62828;";
      default:
        return "background: var(--accent-light); color: var(--text-secondary);";
    }
  }}
`;

// Avatar Modal Styles
export const AvatarModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const AvatarModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
`;

export const AvatarModalContent = styled.div`
  position: relative;
  background: var(--background-color);
  border-radius: 20px;
  max-width: 700px;
  width: 95%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: var(--shadow-strong);
  z-index: 1001;
  display: flex;
  flex-direction: column;
`;

export const AvatarModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 25px 30px;
  border-bottom: 1px solid var(--accent-light);
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--secondary-color) 100%
  );
`;

export const AvatarModalTitle = styled.h3`
  color: white;
  margin: 0;
  font-size: 20px;
  font-weight: 600;
`;

export const AvatarModalClose = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

export const AvatarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
  gap: 12px;
  padding: 40px;
  max-height: 250px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--accent-light);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--primary-color);
    border-radius: 3px;
  }
`;

export const AvatarOption = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 3px solid
    ${(props) => (props.$selected ? props.$color || "#ff9a9e" : "transparent")};
  background: ${(props) => {
    const color = props.$color || "#ff9a9e";
    return props.$selected
      ? `linear-gradient(145deg, ${color}90, ${color}60, ${color}40)`
      : "linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)";
  }};
  box-shadow: ${(props) => {
    const color = props.$color || "#ff9a9e";
    const colorRgb = color.replace("#", "");
    const r = parseInt(colorRgb.substr(0, 2), 16);
    const g = parseInt(colorRgb.substr(2, 2), 16);
    const b = parseInt(colorRgb.substr(4, 2), 16);

    return props.$selected
      ? `0 8px 25px rgba(${r}, ${g}, ${b}, 0.4), 0 4px 12px rgba(${r}, ${g}, ${b}, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)`
      : "0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)";
  }};
  position: relative;

  /* 3D highlight effect */
  &::before {
    content: "";
    position: absolute;
    top: ${(props) => (props.$selected ? "12%" : "15%")};
    left: ${(props) => (props.$selected ? "12%" : "15%")};
    width: ${(props) => (props.$selected ? "30%" : "25%")};
    height: ${(props) => (props.$selected ? "30%" : "25%")};
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0.3) 60%,
      transparent 80%
    );
    border-radius: 50%;
    z-index: 1;
    pointer-events: none;
    opacity: ${(props) => (props.$selected ? "1" : "0.7")};
  }

  &:hover {
    transform: scale(1.15) translateY(-2px);
    box-shadow:
      0 12px 30px rgba(255, 154, 158, 0.3),
      0 6px 15px rgba(254, 207, 239, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.7);
    background: linear-gradient(145deg, #ff9a9e 0%, #fecfef 50%, #fad0c4 100%);

    &::before {
      opacity: 1;
      top: 12%;
      left: 12%;
      width: 30%;
      height: 30%;
    }
  }

  &:active {
    transform: scale(1.05) translateY(-1px);
    box-shadow:
      0 6px 20px rgba(255, 154, 158, 0.4),
      0 3px 10px rgba(254, 207, 239, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
  }
`;

// New Modal Components
export const ModalSection = styled.div`
  padding: 25px 30px 20px;
  border-bottom: 1px solid var(--accent-light);
`;

export const PreviewSection = styled.div`
  padding: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, #ffeaa7 0%, #fab1a0 100%);
  border-bottom: 1px solid var(--accent-light);
`;

export const AvatarPreview = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 60px;
  background: ${(props) => {
    const color = props.$color || "#ff9a9e";
    return `linear-gradient(145deg, ${color}, ${color}90, ${color}60)`;
  }};
  border: 4px solid white;
  box-shadow:
    0 15px 45px rgba(0, 0, 0, 0.2),
    0 8px 25px rgba(255, 255, 255, 0.1),
    inset 0 2px 0 rgba(255, 255, 255, 0.7);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 15%;
    left: 15%;
    width: 35%;
    height: 35%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0.3) 50%,
      transparent 70%
    );
    border-radius: 50%;
    z-index: 1;
    pointer-events: none;
  }
`;

export const ColorPicker = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(45px, 1fr));
  gap: 10px;
  padding: 0;
`;

export const ColorOption = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  cursor: pointer;
  background: ${(props) => props.$color};
  border: 3px solid ${(props) => (props.$selected ? "white" : "transparent")};
  box-shadow: ${(props) =>
    props.$selected
      ? "0 0 0 3px var(--primary-color), 0 6px 20px rgba(0, 0, 0, 0.2)"
      : "0 3px 12px rgba(0, 0, 0, 0.15)"};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 15%;
    left: 15%;
    width: 30%;
    height: 30%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.8) 0%,
      transparent 70%
    );
    border-radius: 50%;
    z-index: 1;
    pointer-events: none;
  }

  &:hover {
    transform: scale(1.1) translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.25);
  }

  &:active {
    transform: scale(1.05);
  }
`;

export const ModalActions = styled.div`
  padding: 25px 30px;
  display: flex;
  gap: 15px;
  justify-content: center;
  background: var(--card-background);
`;

export const SaveAvatarButton = styled.button`
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--secondary-color) 100%
  );
  color: white;
  border: none;
  border-radius: 25px;
  padding: 12px 25px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(124, 14, 51, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(124, 14, 51, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const CancelAvatarButton = styled.button`
  background: var(--accent-light);
  color: var(--text-secondary);
  border: none;
  border-radius: 25px;
  padding: 12px 25px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: var(--text-secondary);
    color: white;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

// New Avatar Customization Components
export const AvatarCustomizationForm = styled.div`
  background: var(--card-background);
  border-radius: 20px;
  padding: 30px;
  box-shadow: var(--shadow-soft);
  border: 1px solid var(--accent-light);
`;

export const AvatarPreviewCard = styled.div`
  background: linear-gradient(135deg, #ffeaa7 0%, #fab1a0 100%);
  border-radius: 15px;
  padding: 25px;
  text-align: center;
  margin-bottom: 30px;
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

export const GradientPicker = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 15px;
  padding: 0;
  margin-top: 15px;
`;

export const GradientOption = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 15px;
  cursor: pointer;
  background: ${(props) => props.$gradient};

  outline: ${(props) =>
    props.$selected ? "3px solid #ff9a9e" : "3px solid  #ff9a9e00"};
  box-shadow: ${(props) =>
    props.$selected
      ? "0 0 0 3px var(--primary-color), 0 6px 20px rgba(0, 0, 0, 0.2)"
      : "0 3px 12px rgba(0, 0, 0, 0.15)"};

  box-shadow: ${(props) => {
    const color = "#ff9a9e";
    const colorRgb = color.replace("#", "");
    const r = parseInt(colorRgb.substr(0, 2), 16);
    const g = parseInt(colorRgb.substr(2, 2), 16);
    const b = parseInt(colorRgb.substr(4, 2), 16);

    return props.$selected
      ? `0 8px 25px rgba(${r}, ${g}, ${b}, 0.4), 0 4px 12px rgba(${r}, ${g}, ${b}, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6)`
      : "0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)";
  }};

  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 15%;
    left: 15%;
    width: 30%;
    height: 30%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.8) 0%,
      transparent 70%
    );
    border-radius: 50%;
    z-index: 1;
    pointer-events: none;
  }

  &:hover {
    transform: scale(1.1) translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  }

  &:active {
    transform: scale(1.05) translateY(-1px);
  }
`;

// Scattered Avatar Pattern Components
export const ScatteredAvatars = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
`;

export const FloatingAvatar = styled.span`
  position: absolute;
  font-size: ${(props) => props.$size || "16px"};
  top: ${(props) => props.$top || "50%"};
  left: ${(props) => props.$left || "50%"};
  transform: rotate(${(props) => props.$rotate || "0deg"});
  filter: blur(${(props) => props.$blur || "0px"})
    ${(props) => props.$glow || "none"};
  opacity: ${(props) => props.$opacity || "0.15"};
  animation: ${(props) => `avatarFloat${props.$index || 0}`}
    ${(props) => 8 + (props.$index || 0) * 0.5}s ease-in-out infinite alternate;
  animation-delay: ${(props) => (props.$index || 0) * 0.2}s;

  @keyframes ${(props) => `avatarFloat${props.$index || 0}`} {
    0% {
      transform: rotate(${(props) => props.$rotate || "0deg"}) translateY(0px)
        scale(1);
    }
    50% {
      transform: rotate(${(props) => props.$rotate || "0deg"})
        translateY(-${(props) => 2 + ((props.$index || 0) % 3)}px)
        scale(${(props) => 1 + (props.$index || 0) * 0.01});
    }
    100% {
      transform: rotate(${(props) => props.$rotate || "0deg"})
        translateY(${(props) => 1 + ((props.$index || 0) % 2)}px)
        scale(${(props) => 0.98 + (props.$index || 0) * 0.01});
    }
  }
`;
